/* eslint-disable indent */
/* eslint-disable react/jsx-indent */

import { useEffect, useState } from 'react';
import { FiFilter, FiSearch } from 'react-icons/fi';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Avatar,
  Heading,
  Spinner,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from '@chakra-ui/react';
import { ProviderCardNew } from '../../components';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updateHttpsProvider } from '../../redux/httpsProviders/actions';
import { getAllProvidersWithPagination } from '../../utils/app-builder/fetch-providers';
import { GET_OWNER_DETAILS, GET_USER_DETAILS, SEARCH_USERS } from '../../utils/constants';
import { formatDate } from '../../utils/format-date';
import { useDebounce } from '../../utils/logs/send-logs';
import { getFirebaseIdToken } from '../../utils/utils';
import { ProviderDetails } from '../explore/components/provider-details.component';
import { IoSearchSharp } from 'react-icons/io5';
import { AdminApplicationCard } from '../../components/application-card/admin-application-card.component';
import { ApplicationCard } from '../../components/application-card/application-card.component';
import { selectCompleteUserDetails } from '../../redux/admin/userDetails/selector';
import { selectFetchedUsers } from '../../redux/admin/userDetails/selector';
import { setCompleteUserDetails } from '../../redux/admin/userDetails';
import { setFetchedUsers } from '../../redux/admin/userDetails';

export const AdminDashboardScreen = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [providerTypeFilter, setProviderTypeFilter] = useState('ALL');
  const [providerStatusFilter, setProviderStatusFilter] = useState('ALL');
  const [providerVisibilityFilter, setProviderVisibilityFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [myProviderOptions, setMyProviderOptions] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProviderData, setSelectedProviderData] = useState<any>(null);

  const [searchType, setSearchType] = useState<'application' | 'provider'>('application');
  const [searchedId, setSearchedId] = useState('');
  const [ownerDetails, setOwnerDetails] = useState<{
    loading: boolean;
    ownerDetails: any;
  }>({ loading: false, ownerDetails: null });

  const [searchedUserQuery, setSearchedUserQuery] = useState('');
  const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOwnerDetailsModalOpen,
    onOpen: onOwnerDetailsModalOpen,
    onClose: onOwnerDetailsModalClose,
  } = useDisclosure();

  const { width, height } = useWindowDimensions();

  const fetchedUsers = useAppSelector(selectFetchedUsers);
  const completeUserDetails = useAppSelector(selectCompleteUserDetails);

  const debouncedSearchTerm = useDebounce(searchQuery, 500);

  const pageSize = 20;

  const fetchProviderData = async (pageKey: number) => {
    const sortByCreatedLatest = true;
    const myProviders = await getAllProvidersWithPagination(
      pageKey * pageSize,
      pageSize,
      searchQuery,
      providerTypeFilter,
      providerStatusFilter,
      providerVisibilityFilter,
      sortByCreatedLatest
    );
    setMyProviderOptions([...myProviders?.providers]);
    setTotalPages(Math.ceil(myProviders?.count / pageSize));
  };

  useEffect(() => {
    fetchProviderData(currentPage - 1);
  }, [
    debouncedSearchTerm,
    currentPage,
    providerTypeFilter,
    providerStatusFilter,
    providerVisibilityFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    providerTypeFilter,
    providerStatusFilter,
    providerVisibilityFilter,
  ]);

  useEffect(() => {
    if (completeUserDetails && completeUserDetails?.userDetails?.uid) {
      showCompleteUserDetails(completeUserDetails?.userDetails);
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          className="x-small-font"
          onClick={() => handlePageChange(i)}
          bgColor={currentPage === i ? 'whitesmoke' : 'white'}
        >
          {i}
        </Button>
      );
    }

    return pages;
  };

  const handleProviderDelete = async (httpProviderId: string) => {
    const status: any = await dispatch(
      updateHttpsProvider({
        httpProviderId: httpProviderId,
        formData: { isActive: false },
      })
    );

    if (status?.payload?.isSuccess) {
      toast({
        title: 'Provider status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchProviderData(currentPage - 1);
    }
  };

  const showProviderDetails = (providerId: string) => {
    const providerData = myProviderOptions.find(
      (provider: any) => provider.httpProviderId === providerId
    );
    setSelectedProviderData(providerData);
    onOpen();
  };

  const handleOwnerSearch = async () => {
    try {
      setOwnerDetails({ loading: true, ownerDetails: null });
      const accessToken = await getFirebaseIdToken();

      const response = await fetch(GET_OWNER_DETAILS(searchedId, searchType), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (response.status === 404) {
        throw new Error('Owner not found');
      } else if (response.status !== 200) {
        throw new Error('Error fetching owner details');
      }

      const data = await response.json();
      if (data?.isSuccess) {
        setOwnerDetails({ loading: false, ownerDetails: data.data });
        onOwnerDetailsModalOpen();
      } else {
        throw new Error('Error fetching owner details');
      }
    } catch (error: any) {
      toast({
        title: error.message || 'Error fetching owner details',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setOwnerDetails({ loading: false, ownerDetails: null });
    }
  };

  const handleUserSearch = async (e: any) => {
    e?.preventDefault();

    const trimmedQuery = searchedUserQuery.trim();

    if (trimmedQuery.length < 3) {
      toast({
        title: 'Please enter at least 3 characters to search',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    try {
      setIsUserSearchLoading(true);
      const accessToken = await getFirebaseIdToken();
      const response = await fetch(SEARCH_USERS(trimmedQuery), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (data?.isSuccess) {
        dispatch(setFetchedUsers(data?.data));
        toast({
          title: 'User details fetched successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        throw new Error('Error fetching user details');
      }
    } catch (error: any) {
      console.log('error', error);
      toast({
        title: error?.message || 'Error fetching user details',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsUserSearchLoading(false);
    }
  };

  const showCompleteUserDetails = async (userDetails: any) => {
    try {
      const userId = userDetails?.uid;
      const accessToken = await getFirebaseIdToken();
      const response = await fetch(GET_USER_DETAILS(userId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (data?.isSuccess) {
        dispatch(setCompleteUserDetails({ ...data?.data, userDetails }));
      } else {
        throw new Error('Error fetching user details');
      }
    } catch (error: any) {
      console.log('error', error);
      toast({
        title: error?.message || 'Error fetching user details',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const showUserProviderDetails = (provider: any) => {
    setSelectedProviderData(provider);
    onOpen();
  };

  return (
    <Flex h="fit-content" justifyContent="center" width="100%" direction="column">
      <Card w={width > 1740 ? '1300px' : '96%'}>
        <CardBody>
          <VStack align="flex-start" spacing="4px" mb={2}>
            <Text
              fontFamily="inter"
              className="medium-font"
              fontWeight="bold"
              color="black"
            >
              Search Owner Details
            </Text>
          </VStack>
          <Flex w="100%" justifyContent="space-between" gap={10}>
            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray" />
              </InputLeftElement>
              <Input
                size="md"
                value={searchedId}
                onChange={(e) => setSearchedId(e.target.value)}
                type="search"
                placeholder="0x1234567890123456789012345678901234567890"
                className="small-font"
                borderRadius="8px"
                variant="filled"
                bgColor="#f3f5fc"
              />
            </InputGroup>
            <Select
              flex={0.5}
              value={searchType}
              onChange={(e) =>
                setSearchType(e.target.value as 'application' | 'provider')
              }
            >
              <option value="application">Application</option>
              <option value="provider">Provider</option>
            </Select>
            <Button
              flex={0.5}
              maxW="200px"
              colorScheme="blue"
              rightIcon={<FiFilter size={18} color="black" />}
              className="small-font"
              borderRadius="8px"
              variant="outline"
              size="md"
              fontWeight={500}
              color="black"
              isDisabled={!searchedId || !searchType}
              isLoading={ownerDetails?.loading}
              onClick={handleOwnerSearch}
            >
              Search
            </Button>
          </Flex>
        </CardBody>
      </Card>
      <Card mt={6} w={width > 1740 ? '1300px' : '96%'}>
        <CardBody position={'relative'}>
          {isUserSearchLoading && (
            <Flex
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="whiteAlpha.800"
              zIndex="1"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Flex>
          )}
          <VStack align="flex-start" spacing="4px" mb={2}>
            <Text
              fontFamily="inter"
              className="medium-font"
              fontWeight="bold"
              color="black"
            >
              Search User Details
            </Text>
          </VStack>
          <form onSubmit={handleUserSearch}>
            <Flex w="100%" justifyContent="space-between" gap={4}>
              <InputGroup flex={1}>
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray" />
                </InputLeftElement>
                <Input
                  size="md"
                  value={searchedUserQuery}
                  onChange={(e) => setSearchedUserQuery(e.target.value)}
                  type="search"
                  placeholder="Search user by email, name, uid etc."
                  className="small-font"
                  borderRadius="8px"
                  variant="filled"
                  bgColor="#f3f5fc"
                />
              </InputGroup>
              <Button
                type="submit"
                maxW="200px"
                colorScheme="blue"
                rightIcon={<IoSearchSharp size={18} color="black" />}
                className="small-font"
                borderRadius="8px"
                variant="outline"
                size="md"
                fontWeight={500}
                color="black"
                isDisabled={!searchedUserQuery}
                isLoading={ownerDetails?.loading}
                onClick={handleUserSearch}
              >
                Search
              </Button>
              {fetchedUsers && (
                <Button
                  colorScheme="gray"
                  className="small-font"
                  borderRadius="8px"
                  variant="outline"
                  size="md"
                  fontWeight={500}
                  onClick={() => {
                    setSearchedUserQuery('');
                    dispatch(setFetchedUsers(null));
                    dispatch(setCompleteUserDetails(null));
                  }}
                >
                  Clear
                </Button>
              )}
            </Flex>
          </form>
          {fetchedUsers && (
            <Flex mt={4} h={'fit-content'}>
              {fetchedUsers.length === 0 ? (
                <Flex
                  w={'100%'}
                  minH={'50px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <Text>No users found</Text>
                </Flex>
              ) : (
                <>
                  {completeUserDetails ? (
                    <Flex
                      direction={'column'}
                      w={'100%'}
                      border={'1px solid black'}
                      p={4}
                      borderRadius={'8px'}
                      minH={'100px'}
                    >
                      <Flex
                        w={'100%'}
                        direction={'column'}
                        mb={4}
                        alignItems={'flex-start'}
                        justifyContent={'space-between'}
                      >
                        <Button
                          leftIcon={<ChevronLeftIcon />}
                          size="sm"
                          onClick={() => dispatch(setCompleteUserDetails(null))}
                        >
                          Back to Results
                        </Button>
                      </Flex>
                      <Tabs variant="enclosed" colorScheme="blue">
                        <TabList>
                          <Tab>
                            Providers ({completeUserDetails?.providers?.length || 0})
                          </Tab>
                          <Tab>
                            Applications ({completeUserDetails?.applications?.length || 0}
                            )
                          </Tab>
                        </TabList>
                        <TabPanels>
                          <TabPanel>
                            {completeUserDetails?.providers?.length > 0 ? (
                              <Box maxH="400px" overflowY="auto" w="100%" pr={2}>
                                <Grid
                                  w="100%"
                                  templateColumns="repeat(auto-fill,minmax(255px,1fr))"
                                  gap={4}
                                >
                                  {completeUserDetails.providers.map((provider: any) => (
                                    <GridItem key={provider.httpProviderId}>
                                      <ProviderCardNew
                                        providerDetail={provider}
                                        pointerCursor={true}
                                        showEditButton={true}
                                        isSelected={false}
                                        showDeleteButton={true}
                                        handleProviderDelete={handleProviderDelete}
                                        onSelect={() => showUserProviderDetails(provider)}
                                        isAdminPanel={true}
                                      />
                                    </GridItem>
                                  ))}
                                </Grid>
                              </Box>
                            ) : (
                              <Text color="gray.500">
                                No providers found for this user
                              </Text>
                            )}
                          </TabPanel>
                          <TabPanel>
                            {completeUserDetails?.applications?.length > 0 ? (
                              <Box maxH="400px" overflowY="auto" w="100%" pr={2}>
                                <Grid
                                  w="100%"
                                  templateColumns="repeat(auto-fill,minmax(280px,1fr))"
                                  gap={4}
                                >
                                  {completeUserDetails.applications.map(
                                    (application: any) => (
                                      <GridItem key={application.appId}>
                                        <ApplicationCard
                                          appDetail={application}
                                          pointerCursor
                                          onSelect={undefined}
                                          fromAdminDashboard
                                        />
                                      </GridItem>
                                    )
                                  )}
                                </Grid>
                              </Box>
                            ) : (
                              <Text color="gray.500">
                                No applications found for this user
                              </Text>
                            )}
                          </TabPanel>
                        </TabPanels>
                      </Tabs>
                      {/* <Heading size="sm" mb={2} ml={1}>User's Providers</Heading>
                          {completeUserDetails?.providers?.length > 0 ? (
                            <Box maxH="400px" overflowY="auto" w="100%" pr={2}>
                              <Grid
                                w='100%'
                                templateColumns='repeat(auto-fill,minmax(255px,1fr))'
                                gap={4}

                              >
                                {completeUserDetails.providers.map((provider: any) => (
                                  <GridItem key={provider.httpProviderId}>
                                    <ProviderCardNew
                                      providerDetail={provider}
                                      pointerCursor={true}
                                      showEditButton={true}
                                      isSelected={false}
                                      showDeleteButton={true}
                                      handleProviderDelete={handleProviderDelete}
                                      onSelect={() => showUserProviderDetails(provider)}
                                      isAdminPanel={true}
                                    />
                                  </GridItem>
                                ))}
                              </Grid>
                            </Box>
                          ) : (
                            <Text color="gray.500">No providers found for this user</Text>
                          )}
                          <Heading size="sm" mt={6} mb={2} ml={1}>User's Applications</Heading>
                          {completeUserDetails?.applications?.length > 0 ? (
                            <Box maxH="400px" overflowY="auto" w="100%" pr={2}>
                              <Grid
                                w='100%'
                                templateColumns='repeat(auto-fill,minmax(280px,1fr))'
                                gap={4}
                              >
                                {completeUserDetails.applications.map((application: any) => (
                                  <GridItem key={application.appId}>
                                    <ApplicationCard
                                      appDetail={application}
                                      pointerCursor
                                      onSelect={undefined}
                                      fromAdminDashboard
                                    />
                                  </GridItem>
                                ))}
                              </Grid>
                            </Box>
                          ) : (
                            <Text color="gray.500">No applications found for this user</Text>
                          )} */}
                    </Flex>
                  ) : (
                    <Grid w={'100%'} templateColumns="repeat(3, 1fr)" gap={4}>
                      {fetchedUsers.map((user: any) => (
                        <Card
                          key={user.uid}
                          minH={'100px'}
                          h={'fit-content'}
                          bgColor={'#f7f9fb'}
                          cursor={'pointer'}
                          borderRadius={'16px'}
                          border={'1.5px solid transparent'}
                          // _hover={{ border: '1.5px solid black' }}
                        >
                          <CardBody>
                            <VStack spacing={2} align="stretch">
                              <Flex align="center" gap={2} mb={2}>
                                <Avatar
                                  name={user.name || user.email || 'No Name'}
                                  src={user.photoURL || ''}
                                  size="sm"
                                />
                                <Text fontWeight="500" noOfLines={1}>
                                  {user.name || 'No Name'}
                                </Text>
                              </Flex>
                              <Text noOfLines={2} className="small-font">
                                <Text as="span" fontWeight={'bold'}>
                                  UID:
                                </Text>{' '}
                                {user.uid}
                              </Text>
                              <Text noOfLines={1} className="small-font">
                                <Text as="span" fontWeight={'bold'}>
                                  Email:
                                </Text>{' '}
                                {user.email || 'No Email'}
                              </Text>
                              <Text mb={2} className="small-font">
                                <Text as="span" fontWeight={'bold'}>
                                  Type:
                                </Text>{' '}
                                {user.type || 'N/A'}
                              </Text>
                              <Button
                                size="sm"
                                colorScheme="blackAlpha"
                                variant="outline"
                                w={'fit-content'}
                                mx={'auto'}
                                color={'black'}
                                className="small-font"
                                _hover={{
                                  bgColor: 'blackAlpha.100',
                                  transform: 'scale(1.1)',
                                  border: '1px solid black',
                                }}
                                onClick={() => showCompleteUserDetails(user)}
                              >
                                View Details
                              </Button>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Flex>
          )}
        </CardBody>
      </Card>
      <Card
        w={width > 1740 ? '1300px' : '96%'}
        mt={6}
        variant="outline"
        borderRadius="12px"
        h="fit-content"
        boxShadow="sm"
      >
        <CardBody p={0}>
          <VStack align="flex-start" padding="32px" spacing="24px">
            <VStack align="flex-start" spacing="16px" w="100%">
              <VStack align="flex-start" spacing="4px">
                <Text
                  fontFamily="inter"
                  className="medium-font"
                  fontWeight="bold"
                  color="black"
                >
                  All Providers
                </Text>
              </VStack>
              <HStack spacing="20px" w="100%" justifyContent="space-between">
                <Flex w="100%" gap={4}>
                  <InputGroup maxW="500px">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray" style={{ marginBottom: 5 }} />
                    </InputLeftElement>
                    <Input
                      size="md"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      type="search"
                      placeholder="Search providers"
                      className="small-font"
                      borderRadius="8px"
                      variant="filled"
                      bgColor="#f3f5fc"
                    />
                  </InputGroup>
                </Flex>
                <Flex alignItems="center" gap={5}>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      colorScheme="blue"
                      rightIcon={<FiFilter size={18} color="black" />}
                      className="small-font"
                      borderRadius="8px"
                      variant="outline"
                      size="md"
                      fontWeight={500}
                      color="black"
                    >
                      Filter Providers
                    </MenuButton>
                    <MenuList minWidth="350px">
                      <Flex direction="column">
                        <Flex>
                          <div
                            style={{
                              borderRight: '1px solid #e2e8f0',
                              width: '50%',
                              padding: '10px',
                            }}
                          >
                            <MenuOptionGroup
                              defaultValue={providerTypeFilter ?? 'ALL'}
                              onChange={(value) => setProviderTypeFilter(value as string)}
                              title="Provider Type"
                              type="radio"
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="VERIFIED">Verified</MenuItemOption>
                              <MenuItemOption value="UNVERIFIED">
                                Not Verified
                              </MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                          <div style={{ width: '50%', padding: '10px' }}>
                            <MenuOptionGroup
                              defaultValue={providerVisibilityFilter ?? 'ALL'}
                              onChange={(value) =>
                                setProviderVisibilityFilter(value as string)
                              }
                              title="Visibility Status"
                              type="radio"
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="PUBLIC">Public</MenuItemOption>
                              <MenuItemOption value="PRIVATE">Private</MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                        </Flex>
                        <Flex borderTop="1px solid #e2e8f0">
                          <div style={{ width: '50%', padding: '10px' }}>
                            <MenuOptionGroup
                              defaultValue={providerStatusFilter ?? 'ALL'}
                              onChange={(value) =>
                                setProviderStatusFilter(value as string)
                              }
                              title="Provider Status"
                              type="radio"
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="ACTIVE">Active</MenuItemOption>
                              <MenuItemOption value="INACTIVE">Inactive</MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                        </Flex>
                      </Flex>
                    </MenuList>
                  </Menu>
                </Flex>
              </HStack>
            </VStack>
            <Divider />
            {myProviderOptions.length === 0 ? (
              <Text className="small-font" fontFamily="inter" color="gray.500">
                No providers found
              </Text>
            ) : (
              <Grid
                w="100%"
                templateColumns="repeat(auto-fill,minmax(255px,1fr))"
                gap={height < 1000 ? '16px' : '24px'}
              >
                {myProviderOptions.map((provider: any) => (
                  <GridItem key={provider.httpProviderId}>
                    <ProviderCardNew
                      providerDetail={provider}
                      pointerCursor={true}
                      showEditButton={true}
                      isSelected={false}
                      showDeleteButton={true}
                      handleProviderDelete={handleProviderDelete}
                      onSelect={showProviderDetails}
                      isAdminPanel={true}
                    />
                  </GridItem>
                ))}
              </Grid>
            )}

            <HStack
              w="100%"
              padding="8px"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
            >
              <Text fontFamily="inter" className="x-small-font" color="gray.600">
                Page {currentPage} of {totalPages}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(1)}
                  isDisabled={currentPage === 1}
                >
                  <ArrowLeftIcon boxSize={3} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                >
                  <ChevronLeftIcon boxSize={4} />
                </Button>
                {renderPageNumbers()}
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                >
                  <ChevronRightIcon boxSize={4} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(totalPages)}
                  isDisabled={currentPage === totalPages}
                >
                  <ArrowRightIcon boxSize={3} />
                </Button>
              </HStack>
            </HStack>
            {selectedProviderData && (
              <ProviderDetails
                onClose={onClose}
                isOpen={isOpen}
                providerDetail={selectedProviderData}
              />
            )}
          </VStack>
        </CardBody>
      </Card>
      {ownerDetails?.ownerDetails && (
        <Modal
          isOpen={isOwnerDetailsModalOpen}
          onClose={onOwnerDetailsModalClose}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Owner Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box
                mt={0}
                mb={4}
                borderWidth="1px"
                borderRadius="lg"
                borderColor="gray.200"
                overflow="hidden"
                fontFamily="inter"
              >
                <Table variant="simple">
                  <Tbody>
                    {[
                      {
                        label: 'UID',
                        value: ownerDetails?.ownerDetails?.uid || '-',
                      },
                      {
                        label: 'Name',
                        value: ownerDetails?.ownerDetails?.name || '-',
                      },
                      {
                        label: 'Email',
                        value: ownerDetails?.ownerDetails?.email || '-',
                      },
                      {
                        label: 'User Type',
                        value: ownerDetails?.ownerDetails?.type || '-',
                      },
                      {
                        label: 'Created At',
                        value: formatDate(ownerDetails?.ownerDetails?.createdAt) || '-',
                      },
                    ].map(({ label, value }, index) => (
                      <Tr key={index}>
                        <Td
                          fontWeight="bold"
                          width="30%"
                          verticalAlign="top"
                          fontFamily="inter"
                          className="small-font"
                        >
                          {label}
                        </Td>
                        <Td>
                          <Text fontFamily="inter" className="small-font">
                            {value}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Flex>
  );
};
